// Library Methods
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import CustomIconButton from "src/components/shared/buttons/CustomIconButton.tsx/CustomIconButton";
import ExpandableSearchbar from "src/components/shared/inputs/ExpandableSearchbar/ExpandableSearchbar";
import TableSkeleton from "src/components/containers/shared/TableSkeleton/TableSkeleton";
import ErrorText from "src/components/shared/text/ErrorText/ErrorText";
import PresetExamButtons from "./PresetExamButtons/PresetExamButtons";
import CustomPagination from "src/components/shared/pagination/CustomPagination/CustomPagination";
import TableHeader from "src/components/shared/tables/TableHeader/TableHeader";
import ExamTransferModal from "src/components/containers/shared/ExamTransferModal/ExamTransferModal";
import DeleteExamModal from "./DeleteExamModal/DeleteExamModal";
import ExamSlider from "src/components/containers/shared/ExamSlider/ExamSlider";
import ExamCard from "./ExamCard/ExamCard";
import { PlusIcon, TrashIcon } from "src/assets/icons";

// Hooks
import useDebounce from "src/hooks/input/useDebounce";
import { useExamsData } from "src/services/exams/hooks/useExamsData";
import { useExamStatus } from "src/hooks/exams/useExamStatus";
import { useDeleteExams } from "src/services/exams/hooks/useDeleteExams";
import { useTransferExam } from "src/services/exams/hooks/useTransferExam";
import { useLinkExamDevice } from "src/services/devices/hooks/useLinkExamDevice";
import { useUnlinkExamFromDevice } from "src/services/devices/hooks/useUnlinkExamDevice";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";
import { useDevicesContext } from "src/contexts/devices/DevicesContext";
import { useExamSliderContext } from "src/contexts/exams/ExamSliderContext";
import { useExamTransferContext } from "src/contexts/exams/ExamTransferContext";

// Utils
import {
  getItemsToRender,
  getPageCount,
} from "src/utils/pagination/paginationHelper";
import {
  getAllExamSections,
  hasEmptySimulation,
  getExamCardText,
  getReportEyeText,
  isMultiExamPackage,
} from "src/utils/exams/examReportsHelper";
import { multiKeyCompare } from "src/utils/tables/tableHelper";
import { getLocalDate, getStartOfToday } from "src/utils/date/dateHelper";
import { getTruncatedText } from "src/utils/text/textHelper";

// Styles
import { CustomIconButtonDark } from "src/styles/mui/muiStylesHelper";

// Types
import { OrderType } from "src/types/sharedTypes";
import { ExamType } from "src/services/exams/types";
import { PatientType } from "src/services/patients/types";

// Styles
import { UI_SIZES } from "src/constants/uiTokens";

const MAX_NUM_OF_ROWS = 4;

type ExamsType = {
  selectedPatient: PatientType;
};

type ExtendedExamType = ExamType & {
  examStatus: string;
  examEyeOrder: string;
  examAlgorithm: string;
  fullExamAlgorithm: string;
  examSchedule: string;
};

const Exams = ({ selectedPatient }: ExamsType) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { clinicInfo } = useClinicContext();
  const { isLoadingDevices } = useDevicesContext();

  const patientId = useMemo(() => selectedPatient.id, [selectedPatient.id]);

  const { getExamStatus } = useExamStatus();

  const { actions: sliderActions } = useExamSliderContext();
  const { addExamWrapper, setCurrentPatient } = sliderActions;

  const { openTransferModal, actions: transferActions } =
    useExamTransferContext();
  const { handleTransferClose } = transferActions;

  const { mutate: transferMutate, isPending: isTransferPending } =
    useTransferExam(clinicInfo?.id);
  const { isPending: isLinkPending } = useLinkExamDevice(clinicInfo?.id);
  const { isPending: isUnlinkPending } = useUnlinkExamFromDevice(
    clinicInfo?.id
  );

  const { mutate: deleteMutate, isPending: isDeletePending } = useDeleteExams(
    clinicInfo?.id,
    patientId
  );

  const {
    data: examsData,
    isLoading: isLoadingExams,
    isError: isErrorExams,
    refetch: refetchExams,
  } = useExamsData(clinicInfo?.id, patientId);

  const [isLoadingSlider, setIsLoadingSlider] = useState(false);

  const isLoading = useMemo(
    () =>
      isLoadingExams ||
      isLoadingDevices ||
      isDeletePending ||
      isLoadingSlider ||
      isTransferPending ||
      isLinkPending ||
      isUnlinkPending,
    [
      isLoadingExams,
      isLoadingDevices,
      isDeletePending,
      isLoadingSlider,
      isTransferPending,
      isLinkPending,
      isUnlinkPending,
    ]
  );

  const hasExams = useMemo(
    () => !isLoading && !isErrorExams && examsData && examsData?.length > 0,
    [isLoading, isErrorExams, examsData]
  );

  const hasNoExams = useMemo(
    () => !isLoading && !isErrorExams && !examsData?.length,
    [isLoading, isErrorExams, examsData]
  );

  const [openAddExamSlider, setOpenAddExamSlider] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState<Array<string>>([]);

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedTerm = useDebounce(searchTerm);

  const [order, setOrder] = useState<OrderType>("desc");
  const [orderByOptions, setOrderByOptions] = useState<
    Array<keyof ExtendedExamType>
  >(["creationDate"]);

  const orderByStatus: Array<keyof ExtendedExamType> = ["examStatus"];
  const orderByEye: Array<keyof ExtendedExamType> = ["examEyeOrder"];
  const orderByAlgorithm: Array<keyof ExtendedExamType> = ["examAlgorithm"];
  const orderByScheduled: Array<keyof ExtendedExamType> = ["examSchedule"];

  const headCells = [
    {
      id: "checkbox",
      gridSize: 1,
      orderUsing: [],
      isCheckbox: true,
    },
    {
      id: "status",
      label: t("Status"),
      gridSize: 2,
      orderUsing: [...orderByStatus, ...orderByAlgorithm],
    },
    {
      id: "eye",
      label: t("Eye"),
      gridSize: 2,
      orderUsing: [...orderByEye, ...orderByAlgorithm],
    },
    {
      id: "type",
      label: t("Type"),
      gridSize: 2.5,
      orderUsing: [...orderByAlgorithm, ...orderByScheduled],
    },
    {
      id: "scheduled",
      label: t("Scheduled"),
      gridSize: 2.5,
      orderUsing: [...orderByScheduled, ...orderByAlgorithm],
    },
    {
      id: "extra-cell",
      gridSize: 2,
      orderUsing: [],
    },
  ];

  const formattedExams: Array<ExtendedExamType> = useMemo(
    () =>
      (examsData ?? [])?.map((exam) => {
        return {
          ...exam,
          examStatus: getExamStatus({ exam })?.status ?? t("Not Started"),
          // Translated (e.g. "Both" -> "Binocular") so searching matches
          // what's actually displayed.
          examEyeOrder: isMultiExamPackage(exam)
            ? ""
            : getAllExamSections(exam)
                .map((e) => getReportEyeText(e.eye, t))
                .join(" "),
          examAlgorithm: getExamCardText({ exam, t }),
          fullExamAlgorithm: getExamCardText({ exam, t, isAbbrev: false }),
          examSchedule: getLocalDate(
            exam?.scheduledDate ?? getStartOfToday(clinicInfo?.timezone),
            clinicInfo?.timezone
          ),
        };
      }),
    [examsData, clinicInfo?.timezone, getExamStatus, t]
  );

  // Filtered rows
  const filteredRows = useMemo(() => {
    let filterArray = formattedExams
      // filter the exams with no sections, or a simulation with nothing picked
      ?.filter(
        (exam) =>
          getAllExamSections(exam)?.length > 0 && !hasEmptySimulation(exam)
      )
      ?.filter((exam) => {
        const debouncedLowerCase = debouncedTerm?.toLowerCase();

        const searchTermHasStatus = exam?.examStatus
          ?.toString()
          ?.toLowerCase()
          ?.includes(debouncedLowerCase);

        const searchTermHasEye = exam?.examEyeOrder
          ?.toString()
          ?.toLowerCase()
          ?.includes(debouncedLowerCase);

        const searchTermHasAlgorithm = exam?.examAlgorithm
          ?.toString()
          ?.toLowerCase()
          ?.includes(debouncedLowerCase);

        const searchTermHasFullAlgorithm = exam?.fullExamAlgorithm
          ?.toString()
          ?.toLowerCase()
          ?.includes(debouncedLowerCase);

        const searchTermHasSchedule = exam?.examSchedule
          ?.toString()
          ?.toLowerCase()
          ?.includes(debouncedLowerCase);

        return (
          searchTermHasStatus ||
          searchTermHasEye ||
          searchTermHasAlgorithm ||
          searchTermHasFullAlgorithm ||
          searchTermHasSchedule
        );
      });

    return multiKeyCompare<ExtendedExamType>(
      filterArray,
      order === "asc",
      orderByOptions
    );
  }, [formattedExams, order, orderByOptions, debouncedTerm]);

  const rowsToRender = useMemo(() => {
    return getItemsToRender(filteredRows ?? [], page, MAX_NUM_OF_ROWS);
  }, [page, filteredRows]);

  const hasNoSearchResults = useMemo(
    () => debouncedTerm?.length > 0 && !filteredRows?.length,
    [debouncedTerm, filteredRows]
  );

  // Disable the delete button if some of the exams have linked devices
  const isDeleteDisabled = useMemo(
    () =>
      selectedForDelete.length > 0 &&
      selectedForDelete.some((selectedId) => {
        const selectedExam = examsData?.find((exam) => exam.id === selectedId);
        return selectedExam?.associatedDevice;
      }),
    [examsData, selectedForDelete]
  );

  // Clear searchbar
  const clearSearchFilter = () => {
    setSearchTerm("");
  };

  // Searchbar change
  const onSearchChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (page > 1) setPage(1);
    setSearchTerm(event?.target?.value);
  };

  const handleRequestSort = useCallback(
    (
      _: React.MouseEvent<HTMLButtonElement>,
      property: Array<keyof ExtendedExamType>
    ) => {
      const isAsc =
        orderByOptions.length &&
        orderByOptions[0] === property[0] &&
        order === "asc";

      setOrder(isAsc ? "desc" : "asc");
      setOrderByOptions(property);
    },
    [order, orderByOptions]
  );

  // Handle checkbox clicks
  const handleCheckboxClick = useCallback(
    (_: React.MouseEvent<HTMLButtonElement, MouseEvent>, id: string) => {
      const selectedIndex = selectedForDelete.indexOf(id);
      let newSelected: Array<string> = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selectedForDelete, id);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selectedForDelete.slice(1));
      } else if (selectedIndex === selectedForDelete.length - 1) {
        newSelected = newSelected.concat(selectedForDelete.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selectedForDelete.slice(0, selectedIndex),
          selectedForDelete.slice(selectedIndex + 1)
        );
      }

      setSelectedForDelete(newSelected);
    },
    [selectedForDelete, setSelectedForDelete]
  );

  // Selects all exam card checkboxes
  const handleSelectAllClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      const isChecked = (event.target as HTMLInputElement).checked;

      // select all
      if (isChecked) {
        const selectedIds = rowsToRender.map((n) => n.id);
        setSelectedForDelete([...selectedIds]);
      }
      // deselect all
      else {
        setSelectedForDelete([]);
      }
    },
    [rowsToRender, setSelectedForDelete]
  );

  // Pagination change
  const onPaginationChange = useCallback(
    (_: React.ChangeEvent<unknown>, value: number) => {
      setPage(value);
    },
    [setPage]
  );

  // reset the current page when changing patients
  useEffect(() => {
    setPage(1);
    clearSearchFilter();
  }, [selectedPatient.id]);

  const deleteExam = useCallback(() => {
    deleteMutate(selectedForDelete, {
      onSuccess: () => {
        setSelectedForDelete([]);
        setOpenDeleteModal(false);
      },
    });
  }, [deleteMutate, selectedForDelete]);

  const confirmAddExam = useCallback(async () => {
    setIsLoadingSlider(true);

    try {
      await addExamWrapper(selectedPatient.id);
      await refetchExams();
      setOpenAddExamSlider(false);
    } catch (error) {
      // do nothing
    } finally {
      setIsLoadingSlider(false);
    }
  }, [addExamWrapper, refetchExams, selectedPatient.id]);

  const confirmTransferExam = useCallback(
    (props: {
      patientId: string;
      examId: string | undefined;
      newPatientId: string;
    }) => {
      transferMutate(
        { ...props },
        {
          onSuccess: () => {
            refetchExams();
            handleTransferClose();
          },
        }
      );
    },
    [transferMutate, handleTransferClose, refetchExams]
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          justifyContent: "flex-end",
          marginBottom: "8px",
        }}
      >
        <Box sx={{ display: "flex", gap: "8px" }}>
          {(hasExams || isLoading) && (
            <ExpandableSearchbar
              id="exams-searchbar"
              isLoading={isLoading}
              searchTerm={searchTerm}
              onChange={onSearchChange}
              onClear={clearSearchFilter}
              searchTooltip={t("Search Exams")}
            />
          )}
          <CustomIconButton
            isLoading={isLoading}
            tooltipTitle={t("Add Exam")}
            onClick={() => {
              setCurrentPatient(selectedPatient);
              setOpenAddExamSlider(true);
            }}
            icon={<PlusIcon />}
          />
          {selectedForDelete?.length > 0 && (
            <CustomIconButton
              isLoading={isLoading}
              isDisabled={isDeleteDisabled}
              variant="error"
              tooltipTitle={t("Delete Exam", {
                count: selectedForDelete?.length,
              })}
              onClick={() => setOpenDeleteModal(true)}
              icon={<TrashIcon />}
            />
          )}
        </Box>
      </Box>

      {/* Exam package buttons */}
      <PresetExamButtons
        patientId={patientId}
        isLoadingExams={isLoading}
        refetchExams={refetchExams}
      />

      {/* Loading skeletons */}
      {isLoading && <TableSkeleton numOfRows={MAX_NUM_OF_ROWS} />}

      {/* Exam cards */}
      {hasExams && !hasNoSearchResults && (
        <Box display="flex" flexDirection="column" gap="12px">
          <Grid container item xs={12}>
            <Grid item xs={1.5} />
            <Grid item xs={10.5}>
              <TableHeader<ExtendedExamType>
                headCells={headCells}
                order={order}
                orderBy={orderByOptions?.[0] ?? "creationDate"}
                onRequestSort={handleRequestSort}
                onSelectAllClick={handleSelectAllClick}
                rowCount={rowsToRender?.length}
                numSelected={selectedForDelete?.length}
              />
            </Grid>
          </Grid>
          {rowsToRender?.map((exam) => (
            <ExamCard
              key={exam.id}
              exam={exam}
              selectedPatient={selectedPatient}
              isSelectedForDeletion={selectedForDelete.includes(exam?.id)}
              handleCheckboxClick={handleCheckboxClick}
              refetchExams={refetchExams}
            />
          ))}
          <Box mt="12px">
            <CustomPagination
              page={page}
              count={getPageCount(filteredRows?.length, MAX_NUM_OF_ROWS)}
              onChange={onPaginationChange}
            />
          </Box>
        </Box>
      )}

      {/* Slider to add a new exam */}
      <ExamSlider
        key="add-exam-slider"
        title={`${t("Add Exam")} / ${getTruncatedText(`${selectedPatient.firstName} ${selectedPatient.lastName}`)} / ${selectedPatient?.dateOfBirth?.slice(0, 10)}`}
        isOpen={openAddExamSlider}
        isLoading={isLoading}
        onClose={() => setOpenAddExamSlider(false)}
        onConfirm={confirmAddExam}
      />

      <ExamTransferModal
        open={openTransferModal}
        currentPatient={selectedPatient}
        onClose={handleTransferClose}
        onConfirm={confirmTransferExam}
      />

      <DeleteExamModal
        open={openDeleteModal}
        patientId={patientId}
        selectedIds={selectedForDelete}
        onClose={() => setOpenDeleteModal(false)}
        onConfirm={deleteExam}
      />

      {/* Error messages */}
      {!hasNoExams && hasNoSearchResults && (
        <ErrorText
          header={t("No Exams Found.")}
          message={t("Change your search term to see more results.")}
        />
      )}
      {hasNoExams && (
        <ErrorText
          header={t("Patient Has No Exams.")}
          message={
            <Typography maxWidth="400px">
              {t("Click the")}{" "}
              <IconButton
                sx={{
                  ...CustomIconButtonDark(theme),
                  width: `${UI_SIZES.iconButtonSize}px`,
                  height: `${UI_SIZES.iconButtonSize}px`,
                  pointerEvents: "none",
                }}
              >
                <PlusIcon />
              </IconButton>{" "}
              {t("button above to create an exam.")}
            </Typography>
          }
        />
      )}
    </Box>
  );
};

export default Exams;
